import { css } from "@emotion/react";
import { useForm } from "@tanstack/react-form";
import { enqueueSnackbar } from "notistack";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Field, FieldError, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { useLocaleState } from "../hooks/useLocaleState";
import { useTranslate } from "../hooks/useTranslate/useTranslate";
import { storefrontSdk } from "../storefrontsdk";
import { Alert } from "./ui/alert";
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from "./ui/card";

export const ResetPasswordForm = () => {
	const translate = useTranslate();
	const localeState = useLocaleState();
	const [error, setError] = useState<string>("");
	const resetPasswordForm = useForm({
		defaultValues: {
			email: "",
		},
		onSubmit: async ({ value }) => {
			setError("");
			const response = await storefrontSdk.sendResetPasswordEmailAsync({
				EmailTo: value.email,
				Language: localeState.get().language,
			});
			if (response.status === 200) {
				enqueueSnackbar(
					`${translate("Reset password email was sent to")}: ${value.email}`,
					{
						variant: "success",
						autoHideDuration: null,
					},
				);
			} else {
				setError(`${translate("Sending reset password email failed")}.`);
			}
		},
	});
	const getResetFieldError = (errors: readonly unknown[]) => {
		return typeof errors[0] === "string" ? errors[0] : "";
	};

	return (
		<Card className="mx-auto w-80 sm:w-90">
			<CardHeader>
				<CardTitle>{translate("Reset Password")}</CardTitle>
				<CardDescription>
					{translate("Sends an email with a reset password link.")}
				</CardDescription>
			</CardHeader>
			<CardContent>
				<form
					noValidate
					autoComplete="off"
					onSubmit={(event) => {
						event.preventDefault();
						event.stopPropagation();
						void resetPasswordForm.handleSubmit();
					}}
				>
					<div
						css={css`display:flex;justify-content:center;align-items:center;margin:16px 0 2px 0`}
					>
						<div css={css`flex:1;`}>
							<resetPasswordForm.Field
								name="email"
								validators={{
									onSubmit: ({ value }) => {
										const email = value.trim();
										if (email.length === 0) {
											return translate("Email is required");
										}
										if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
											return translate("Enter a valid email address");
										}
										return undefined;
									},
								}}
							>
								{(field) => {
									const showError =
										field.state.meta.isTouched ||
										resetPasswordForm.state.submissionAttempts > 0;
									const errorMessage = getResetFieldError(
										field.state.meta.errors,
									);
									return (
										<Field>
											<FieldLabel
												className="mb-1 inline-block"
												htmlFor="txtEmail"
											>
												{translate("Email")}{" "}
												<span className="text-destructive">*</span>
											</FieldLabel>
											{showError && errorMessage.length > 0 && (
												<FieldError>{errorMessage}</FieldError>
											)}
											<Input
												css={css`flex:1;`}
												id="txtEmail"
												placeholder={translate("Email")}
												type="text"
												autoComplete="username"
												required
												value={field.state.value}
												maxLength={255}
												onChange={(e) =>
													field.handleChange(e.currentTarget.value)
												}
												onBlur={field.handleBlur}
												aria-invalid={showError && errorMessage.length > 0}
											/>
										</Field>
									);
								}}
							</resetPasswordForm.Field>
						</div>
					</div>

					<div className="flex items-center justify-center mt-4">
						<Button
							type="submit"
							variant="default"
							className="w-full"
							disabled={resetPasswordForm.state.isSubmitting}
						>
							{translate("Reset Password")}
						</Button>
					</div>
					{error && (
						<CardFooter>
							<Alert>{error}</Alert>
						</CardFooter>
					)}
				</form>
			</CardContent>
		</Card>
	);
};
